import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, ArrowRight, X } from 'lucide-react';
import { useSubscription } from '../../context/subscription/SubscriptionContext';

const TIER_HIERARCHY = {
  'free': 0,
  'starter': 1,
  'pro': 2,
  'growth': 3,
  'enterprise': 4
};

export const useVoucherLock = () => {
  const { subscription, isTrialExpired, vouchersCreated, vouchersLimit, loading, initialLoaded } = useSubscription();
  const { isLocked: isPlanLocked, loading: isPlanLoading } = usePlanAccess('starter');

  const isLoading = Boolean(loading || (!initialLoaded && !subscription) || isPlanLoading);
  const limitReached = false; // Numerical quota removed - unlimited vouchers allowed
  const isExpired = isTrialExpired || (subscription && (subscription.status === 'expired' || subscription.status === 'cancelled' || subscription.status === 'payment_failed'));

  const isLocked = isLoading ? false : Boolean(isPlanLocked || isExpired);

  return {
    isLocked,
    limitReached: false,
    isExpired: isLoading ? false : isExpired,
    loading: isLoading,
    vouchersCreated,
    vouchersLimit
  };
};

/**
 * Hook to check plan access for any given required plan level.
 * @param {string} requiredPlan - 'starter' | 'pro' | 'growth' | 'enterprise'
 * @returns {object} { hasAccess, isLocked, currentPlanName, requiredPlanName }
 */
export const usePlanAccess = (requiredPlan = 'pro') => {
  const { subscription, isTrialExpired, loading, initialLoaded } = useSubscription();

  const isLoading = Boolean(loading || (!initialLoaded && !subscription));

  // If loading and subscription is null, assume unlocked to avoid flashing locked modal on reload
  if (isLoading) {
    return {
      hasAccess: true,
      isLocked: false,
      loading: true,
      currentPlanName: 'Loading...',
      requiredPlanName: requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)
    };
  }

  // If in active or pending trial, access is granted up to Growth tier (tier 3). Features requiring Enterprise (tier 4) remain locked.
  if (subscription?.status === 'trial' && !isTrialExpired) {
    const reqSlug = requiredPlan.toLowerCase();
    const requiredTier = TIER_HIERARCHY[reqSlug] || 1;
    const hasAccess = requiredTier <= 3; // Trial grants full Growth tier access
    return {
      hasAccess,
      isLocked: !hasAccess,
      loading: false,
      currentPlanName: 'Trial Period - Growth',
      requiredPlanName: requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)
    };
  }

  // If trial has expired or subscription is not active (e.g. expired, cancelled, no plan)
  const isSubActive = subscription && subscription.status === 'active';
  if (!isSubActive) {
    return {
      hasAccess: false,
      isLocked: true,
      loading: false,
      currentPlanName: isTrialExpired ? 'Trial Expired' : (subscription?.status ? `${subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}` : 'No Plan'),
      requiredPlanName: requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)
    };
  }

  const rawSlug = (
    subscription?.plan_id?.slug || 
    subscription?.plan_id?.name || 
    subscription?.plan_name || 
    subscription?.plan || 
    'starter'
  ).toLowerCase();

  const currentPlan = rawSlug.includes('enterprise') ? 'enterprise' :
                      rawSlug.includes('growth') ? 'growth' :
                      rawSlug.includes('pro') ? 'pro' : 'starter';

  const reqSlug = requiredPlan.toLowerCase();
  const currentTier = TIER_HIERARCHY[currentPlan] || 1;
  const requiredTier = TIER_HIERARCHY[reqSlug] || 1;

  const hasAccess = currentTier >= requiredTier;
  const currentPlanName = subscription?.plan_id?.name || subscription?.plan_name || 'Starter';
  const requiredPlanName = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);

  return {
    hasAccess,
    isLocked: !hasAccess,
    loading: false,
    currentPlanName,
    requiredPlanName
  };
};

/**
 * PlanGate Component
 * Wraps any component (toggles, buttons, form fields, cards) to gate/lock it if the user's current
 * subscription plan is below the required plan.
 * 
 * Shows a matching lock badge and a modern Trrip-themed upgrade tooltip on hover,
 * and opens a sleek upgrade modal on tap/click (ideal for mobile devices).
 */
const PlanGate = ({
  requiredPlan = 'pro',
  featureName = '',
  featureKey = '',
  isLocked: isLockedProp,
  tooltipText,
  children,
  showLockIcon = true,
  showBadge = false,
  badgeText,
  className = '',
  wrapperClassName = '',
  popoverAlign = 'center',
  onLockedClick,
  fallback
}) => {
  const navigate = useNavigate();
  const planAccess = usePlanAccess(requiredPlan);
  const voucherLock = useVoucherLock();

  const isVoucherFeature = featureKey === 'vouchers' || featureKey === 'voucher' || featureKey === 'create-voucher' || featureKey === 'voucher-creation';
  
  const isLocked = isLockedProp !== undefined
    ? isLockedProp
    : (isVoucherFeature ? voucherLock.isLocked : planAccess.isLocked);
    
  const hasAccess = !isLocked;
  const currentPlanName = planAccess.currentPlanName;
  const requiredPlanName = isVoucherFeature 
    ? (voucherLock.limitReached ? 'Higher' : 'Starter')
    : planAccess.requiredPlanName;

  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, isAbove: true });
  const containerRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // Clean up any hover timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const renderContent = typeof children === 'function' 
    ? children({ isLocked, hasAccess, currentPlanName, requiredPlanName }) 
    : children;

  // If user has access to this plan level, render children normally
  if (hasAccess) {
    return <>{renderContent}</>;
  }

  // If custom fallback is provided
  if (fallback) {
    return <>{fallback}</>;
  }

  const defaultTooltipText = tooltipText || (
    isVoucherFeature
      ? 'Active subscription required to create vouchers. Upgrade to unlock.'
      : (featureName ? `${featureName} requires the ${requiredPlanName} Plan.` : `Available on ${requiredPlanName} Plan & above. Your current plan is ${currentPlanName}.`)
  );

  const displayBadgeText = badgeText || `${requiredPlanName} PLAN`.toUpperCase();

  const updatePopoverPos = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = 260;
      const centerX = rect.left + rect.width / 2;
      const safeLeft = Math.max(16, Math.min(window.innerWidth - popoverWidth - 16, centerX - popoverWidth / 2));
      
      // Auto-flip below if element is near top of viewport (rect.top < 180)
      const isAbove = rect.top >= 180;
      const top = isAbove ? rect.top - 8 : rect.bottom + 8;
      
      setPopoverPos({ top, left: safeLeft, isAbove });
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    updatePopoverPos();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <>
      <div 
        ref={containerRef}
        className={`relative ${wrapperClassName.includes('flex') || wrapperClassName.includes('block') ? '' : 'inline-flex items-center'} group select-none ${wrapperClassName}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleContainerClick}
      >
        {/* Dimmed & Non-interactive Wrapped Element */}
        <div className={`opacity-65 pointer-events-none transition-opacity ${wrapperClassName.includes('w-full') ? 'w-full' : ''} ${wrapperClassName.includes('h-full') ? 'h-full flex-1 flex flex-col' : ''} ${className}`}>
          {renderContent}
        </div>

        {/* Lock Icon / Badge */}
        {showLockIcon && (
          <span 
            className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-xl bg-teal-50/90 border border-teal-200/90 text-[#067978] text-[11px] font-bold shadow-xs cursor-pointer hover:bg-teal-100/80 transition-all shrink-0"
            title={`Requires ${requiredPlanName} Plan`}
          >
            <Lock size={12} className="text-[#067978] stroke-[2.2]" />
            {showBadge && <span className="text-[10px] font-extrabold uppercase">{displayBadgeText}</span>}
          </span>
        )}
      </div>

      {/* Interactive Tooltip on Hover - Rendered via Portal to avoid overflow/clipping */}
      {isHovered && createPortal(
        <div 
          style={{ 
            position: 'fixed', 
            top: `${popoverPos.top}px`, 
            left: `${popoverPos.left}px`,
            transform: popoverPos.isAbove ? 'translateY(-100%)' : 'none'
          }}
          className={`w-[260px] p-3.5 bg-white text-slate-800 rounded-2xl shadow-[0_12px_32px_rgba(6,121,120,0.18),0_4px_12px_rgba(16,38,58,0.08)] text-left z-[999999] animate-in fade-in zoom-in-95 duration-150 border border-teal-100/90 pointer-events-auto before:content-[''] before:absolute before:left-0 before:right-0 before:h-4 before:w-full ${popoverPos.isAbove ? 'before:top-full' : 'before:bottom-full'}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-50 text-[#067978] text-[10px] font-extrabold uppercase tracking-wide border border-teal-200/60">
              <Crown size={12} className="text-amber-500 fill-amber-400 shrink-0" />
              <span>{`${requiredPlanName} Plan`}</span>
            </span>
            <Lock size={13} className="text-slate-400" />
          </div>

          <p className="text-xs font-medium text-slate-600 leading-relaxed mb-3">
            {defaultTooltipText}
          </p>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsHovered(false);
              if (onLockedClick) onLockedClick();
              else navigate('/subscription');
            }}
            className="w-full py-2 px-3 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm shadow-teal-900/15 active:scale-95 cursor-pointer"
          >
            <span>{`Upgrade to ${requiredPlanName}`}</span>
            <ArrowRight size={13} />
          </button>
        </div>,
        document.body
      )}

      {/* Sleek Mobile & Desktop Tap Upgrade Modal */}
      {isModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl text-center border border-slate-100 relative animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200/80 text-[#067978] flex items-center justify-center mx-auto mb-4 shadow-xs">
              <Crown size={28} className="text-amber-500 fill-amber-400" />
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 border border-teal-200/80 text-[#067978] text-xs font-black uppercase tracking-wider mb-3">
              <Lock size={12} />
              <span>{`${requiredPlanName} Plan Required`}</span>
            </div>

            <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              {featureName || `${requiredPlanName} Plan Feature`}
            </h3>

            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mt-2 max-w-xs mx-auto">
              {tooltipText || `${featureName || 'This feature'} is available on the ${requiredPlanName} Plan. Upgrade your subscription to unlock complete access.`}
            </p>

            <div className="mt-6 space-y-2.5">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  if (onLockedClick) onLockedClick();
                  else navigate('/subscription');
                }}
                className="w-full bg-[#067978] hover:bg-[#056463] text-white py-3 px-5 rounded-2xl font-bold text-xs sm:text-sm inline-flex items-center justify-center gap-2 shadow-md shadow-[#067978]/20 transition-all active:scale-[0.98] cursor-pointer"
              >
                <span>{`Upgrade to ${requiredPlanName}`}</span>
                <ArrowRight size={16} />
              </button>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 py-2.5 px-5 rounded-2xl font-bold text-xs inline-flex items-center justify-center transition-all cursor-pointer"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PlanGate;
